import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useConfirm } from '../../lib/confirm'
import { formatDate, titleCase } from '../../lib/format'
import type { KycDoc, Organization, UserProfile } from '../../types'
import {
  Alert, Avatar, Badge, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { DocIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/user_detail.html + its view
 * admin/routes.py:user_detail(), reusing the gov portal's already-ported
 * GET /gov/users/:uid (api_gov_user_detail confirms super_admin is an
 * allowed caller despite the URL) rather than a separate admin-only route -
 * none exists, and this one returns the exact same
 * {profile, kyc_docs, course_progress, organization} shape the Jinja
 * template was rendered from. */

interface NextOfKin { name?: string; relationship?: string; phone?: string }

interface AdminUserProfile extends UserProfile {
  roles?: string[]
  reports_to_uid?: string
  national_id?: string
  id_document_number?: string
  id_document_front_storage_path?: string
  id_document_back_storage_path?: string
  id_document_url?: string
  id_document_name?: string
  dob?: string
  gender?: string
  next_of_kin?: NextOfKin
}

interface CourseProgressRow {
  course_id?: string
  course_name?: string
  progress_pct?: number
  is_completed?: boolean
}

interface AdminUserDetailResponse {
  ok: boolean
  profile: AdminUserProfile
  kyc_docs: KycDoc[]
  course_progress: CourseProgressRow[]
  organization: Organization | null
}

type Tone = 'danger' | 'info' | 'warning' | 'success'

function roleTone(role?: string): Tone {
  if (role === 'super_admin') return 'danger'
  if (['gov_director', 'gov_regional', 'gov_inspector'].includes(role ?? '')) return 'info'
  if (role === 'team_leader') return 'warning'
  return 'success'
}

/* Same green/red binary + "suspended" -> "Deactivated" relabel as
 * admin/Users.tsx's card pill - kept local rather than shared since these
 * two pages otherwise have nothing else in common to import from. */
function AccountStatusPill({ status }: { status?: string }) {
  const s = status || 'active'
  return <Badge tone={s === 'active' ? 'success' : 'danger'}>{s === 'suspended' ? 'Deactivated' : titleCase(s)}</Badge>
}

export default function AdminUserDetail() {
  const { uid } = useParams<{ uid: string }>()
  const { user: me } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [showMore, setShowMore] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'user', uid],
    queryFn: () => api.get<AdminUserDetailResponse>(`/gov/users/${uid}`),
    enabled: Boolean(uid),
  })

  const reportsToUid = data?.profile?.reports_to_uid
  const reportsToQuery = useQuery({
    queryKey: ['admin', 'user-brief', reportsToUid],
    queryFn: () => api.get<{ ok: boolean; item: UserProfile }>(`/admin/users/${reportsToUid}`),
    enabled: Boolean(reportsToUid),
  })

  const suspend = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status: string }>(`/admin/users/${uid}/suspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user', uid] }),
  })
  const restore = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status: string }>(`/admin/users/${uid}/restore`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user', uid] }),
  })
  const del = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status: string }>(`/admin/users/${uid}/delete`),
    onSuccess: () => navigate('/users'),
  })

  if (!uid) return <Navigate to="/users" replace />
  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/users">Back to users</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const { profile, kyc_docs: kycDocs, course_progress: courseProgress, organization } = data
  const status = profile.status || 'active'
  const isSelf = profile.uid === me?.uid
  const canDeactivate = status !== 'suspended' && status !== 'deleted' && !isSelf
  const canRestore = status === 'suspended' || status === 'deleted'
  const canDelete = !isSelf && status !== 'deleted'
  const nok = profile.next_of_kin

  const reportsToLabel = !reportsToUid ? '—'
    : reportsToQuery.isLoading ? 'Loading…'
    : (reportsToQuery.data?.item.full_name || reportsToQuery.data?.item.email || '—')

  const mutationError = suspend.error ?? restore.error ?? del.error

  return (
    <>
      <div className="page-head">
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <Avatar name={profile.full_name} email={profile.email} size={64} />
          <div>
            <h1>{profile.full_name || 'Unknown user'}</h1>
            <p className="page-head__sub">{profile.email || 'No email'}</p>
            <div className="row" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
              <Badge tone={roleTone(profile.role)}>{titleCase(profile.role)}</Badge>
              <AccountStatusPill status={status} />
            </div>
          </div>
        </div>
        <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
          <Link to="/users"><Button variant="secondary" size="sm">Back</Button></Link>
          <Link to={`/users/${uid}/edit`}><Button size="sm">Edit User</Button></Link>
          {canDeactivate && (
            <Button
              size="sm" variant="danger" loading={suspend.isPending}
              onClick={async () => {
                if (await confirm({
                  title: 'Deactivate this account?',
                  message: 'The user will no longer be able to sign in.',
                  destructive: true,
                })) suspend.mutate()
              }}
            >
              Deactivate Account
            </Button>
          )}
          {canRestore && (
            <Button
              size="sm" variant="secondary" loading={restore.isPending}
              onClick={async () => {
                if (await confirm({
                  title: 'Restore this account?',
                  message: 'The user will be able to sign in again.',
                })) restore.mutate()
              }}
            >
              Restore Account
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm" variant="danger" loading={del.isPending}
              onClick={async () => {
                if (await confirm({
                  title: 'Delete this user?',
                  message: 'They will be hidden from the system and unable to sign in. Their records are '
                    + 'retained and this can be undone by restoring them.',
                  confirmLabel: 'Delete',
                  destructive: true,
                })) del.mutate()
              }}
            >
              Delete User
            </Button>
          )}
        </div>
      </div>

      {mutationError && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="danger">{mutationError instanceof ApiError ? mutationError.message : 'That action failed.'}</Alert>
        </div>
      )}

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Account Profile" />
        <CardBody>
          <div className="grid grid--2">
            <Row label="Full Name" value={profile.full_name || '—'} />
            <Row label="Email" value={profile.email || '—'} />
            <Row label="Phone" value={profile.phone || '—'} />
            <Row label="Role" value={titleCase(profile.role)} />
            <Row label="Additional Roles" value={profile.roles && profile.roles.length ? profile.roles.map((r) => titleCase(r)).join(', ') : '—'} />
            <Row label="Reports To" value={reportsToLabel} />
            <Row label="Tier" value={titleCase(profile.tier)} />
            <Row label="Status" value={status === 'suspended' ? 'Deactivated' : titleCase(status)} />
            <Row label="Organisation" value={organization?.name || profile.org_id || '—'} />
            <Row label="Region" value={titleCase(profile.region_id)} />
            <Row label="District" value={titleCase(profile.district_id)} />
            <Row label="National ID" value={profile.national_id || '—'} />
            <Row label="ID Document Number" value={profile.id_document_number || '—'} />
            <Row label="ID Document" value={<IdDocumentLinks uid={uid} profile={profile} />} />
            <Row label="Date of Birth" value={profile.dob || '—'} />
            <Row label="Gender" value={titleCase(profile.gender)} />
            <Row label="UID" value={<span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{profile.uid || '—'}</span>} />
          </div>
        </CardBody>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--sp-6)' }}>
        <Button variant="secondary" size="sm" onClick={() => setShowMore((s) => !s)}>
          {showMore ? 'Hide extra details' : 'View more details — next of kin, documents, training'}
        </Button>
      </div>

      {showMore && (
        <div className="stack" style={{ gap: 'var(--sp-6)' }}>
          {nok?.name && (
            <Card>
              <CardHeader title="Next of Kin" />
              <CardBody>
                <div className="grid grid--2">
                  <Row label="Name" value={nok.name || '—'} />
                  <Row label="Relationship" value={nok.relationship || '—'} />
                  <Row label="Phone" value={nok.phone || '—'} />
                </div>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader title="Uploaded Documents (KYC)" />
            <CardBody flush>
              {kycDocs.length === 0 ? (
                <div style={{ padding: 'var(--sp-6)' }}>
                  <EmptyState icon={<DocIcon size={22} />} title="No documents uploaded" body="" />
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Type</th><th>Uploaded</th><th>Status</th><th /></tr></thead>
                    <tbody>
                      {kycDocs.map((d, i) => (
                        <tr key={d.id ?? i}>
                          <td>{d.doc_type || 'Document'}</td>
                          <td>{formatDate(d.uploaded_at)}</td>
                          <td><StatusBadge status={d.status || 'pending'} /></td>
                          <td>
                            {(d.file_url || d.url) && (
                              <a href={d.file_url || d.url} target="_blank" rel="noopener noreferrer">View</a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          {courseProgress.length > 0 && (
            <Card>
              <CardHeader title="Course Progress" />
              <CardBody flush>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Course</th><th className="table__num">Progress</th></tr></thead>
                    <tbody>
                      {courseProgress.map((c, i) => (
                        <tr key={c.course_id ?? i}>
                          <td>{c.course_name}</td>
                          <td className="table__num">
                            {c.is_completed ? <StatusBadge status="completed" /> : `${c.progress_pct ?? 0}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550, textAlign: 'right' }}>{value}</span>
    </div>
  )
}

/* Delivered by the session-cookie-authenticated Jinja route
 * admin.user_id_document (/admin/users/<uid>/id/<side>), not a JSON API
 * endpoint - no such endpoint exists under /api/v1. A plain same-origin
 * <a> navigation carries the __session cookie automatically (as
 * ops/StaffDetail.tsx already relies on for its own document links), and
 * firebase.json rewrites /admin/** to the backend, so this reaches the
 * same handler the Jinja template linked to. */
function IdDocumentLinks({ uid, profile }: { uid: string; profile: AdminUserProfile }) {
  const hasFront = Boolean(profile.id_document_front_storage_path)
  const hasBack = Boolean(profile.id_document_back_storage_path)
  if (hasFront || hasBack) {
    return (
      <span style={{ display: 'inline-flex', gap: 'var(--sp-3)' }}>
        {hasFront && <a href={`/admin/users/${uid}/id/front`} target="_blank" rel="noopener noreferrer">Front</a>}
        {hasBack && <a href={`/admin/users/${uid}/id/back`} target="_blank" rel="noopener noreferrer">Back</a>}
      </span>
    )
  }
  if (profile.id_document_url) {
    return <a href={profile.id_document_url} target="_blank" rel="noopener noreferrer">{profile.id_document_name || 'View document'}</a>
  }
  return <>—</>
}
